"use client";

import { motion } from "framer-motion";
import Link from "@/components/LocalizedLink";
import type { ReactElement } from "react";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.08, duration: 0.5 },
  }),
};

const outcomes = [
  {
    title: "400G coherent wavelength end-to-end",
    desc: "A single 400G service delivered from Agarak on the Armenian border through to Frankfurt — one of Europe's most important interconnection hubs — on a modern coherent DWDM platform.",
  },
  {
    title: "Submarine bypass of Türkiye",
    desc: "Traffic crosses the Black Sea on the Caucasus Cable System submarine route between Poti and Balchik, removing dependency on Türkiye terrestrial transit and reducing geopolitical risk on the path.",
  },
  {
    title: "Diverse, protected backbone",
    desc: "The wave is carried over Sofia Connect's fully owned and managed backbone through Balchik → Sofia → Frankfurt, with diverse fiber and 24/7 carrier-grade operations.",
  },
];

const routeSegments = [
  {
    from: "Agarak (AM)",
    to: "Tbilisi (GE)",
    detail:
      "Armenia–Georgia cross-border fiber. Hand-off on the Armenian side at Agarak; transit across Georgia on partner infrastructure.",
  },
  {
    from: "Tbilisi (GE)",
    to: "Poti (GE)",
    detail:
      "Georgian domestic DWDM path westbound to the Black Sea landing station at Poti.",
  },
  {
    from: "Poti (GE)",
    to: "Balchik (BG)",
    detail:
      "Caucasus Cable System submarine link across the Black Sea — the core element that takes the route around Türkiye entirely.",
  },
  {
    from: "Balchik (BG)",
    to: "Sofia (BG)",
    detail:
      "Sofia Connect's own Bulgarian backbone from the Black Sea coast to the Sofia core — a PoP with dense onward reach into Europe.",
  },
  {
    from: "Sofia (BG)",
    to: "Frankfurt (DE)",
    detail:
      "European DWDM leg into Frankfurt — Sofia Connect's primary interconnection gateway to Western Europe, with onward reach to London, Amsterdam, and Paris.",
  },
];

export function TeamTelecomCaseStudyClient(): ReactElement {
  return (
    <main className="min-h-screen pt-20">
      {/* Breadcrumb */}
      <section className="px-4 sm:px-6 lg:px-8 pt-6">
        <div className="mx-auto max-w-4xl">
          <nav className="text-xs text-muted-foreground">
            <Link href="/case-studies" className="hover:text-accent transition-colors">
              Case Studies
            </Link>
            <span className="mx-2">/</span>
            <span className="text-foreground">TEAM Telecom Armenia — 400G DWDM</span>
          </nav>
        </div>
      </section>

      {/* Hero */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        <div className="absolute inset-0 network-grid opacity-30" />
        <div className="relative z-10 mx-auto max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="flex items-center gap-3 mb-5">
              <span className="px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded-full bg-accent/10 text-eyebrow">
                Partnership Story
              </span>
              <span className="text-xs text-muted font-mono">2026 · Q1</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-foreground">
              400G DWDM from Armenia to Frankfurt — with TEAM Telecom Armenia
            </h1>
            <p className="mt-5 text-lg text-muted-foreground leading-relaxed">
              Sofia Connect and TEAM Telecom Armenia brought a 400G coherent
              wavelength into production from Agarak on Armenia&apos;s southern
              border through to Frankfurt — engineered to route around Türkiye
              using the Caucasus Cable System submarine link across the Black Sea.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Challenge · Partner · Outcome */}
      <section className="px-4 sm:px-6 lg:px-8 pb-8">
        <div className="mx-auto max-w-4xl space-y-6">
          {[
            {
              heading: "The Challenge",
              body: "Armenia is a landlocked market with a very particular connectivity problem: most of its wholesale capacity traditionally transits Türkiye or Russia, concentrating risk on a small number of terrestrial paths. For a national carrier like TEAM Telecom Armenia, continuing to grow — and continuing to meet the capacity and resilience expectations of hyperscaler, enterprise, and carrier customers — means adding a genuinely independent westbound route into the European core. The requirement was clear: 400G of coherent capacity, deterministic engineering, and a path that does not depend on Türkiye.",
            },
            {
              heading: "The Partner",
              body: "TEAM Telecom Armenia is one of Armenia's leading telecom operators and runs a substantial national backbone, with long-standing cross-border fiber into Georgia. Sofia Connect operates a regional DWDM backbone across Southeast Europe — Bulgaria, the Black Sea coast, and onward into Frankfurt — and owns diverse infrastructure into Balchik, where the Caucasus Cable System lands on the Bulgarian shore. Together the two networks form a natural end-to-end path from the Armenian border to Western Europe, combining regional reach on each side with a submarine leg that genuinely avoids Türkiye.",
            },
            {
              heading: "The Outcome",
              body: "A single 400G wavelength, live in Q1 2026, delivered from Agarak through Tbilisi and Poti, across the Black Sea on CCS, and westbound through Balchik, Sofia, and onward to Frankfurt. The service is carried on Sofia Connect's own backbone through the Bulgarian segment, with diverse fiber routes and a standard carrier-grade operations model. For TEAM Telecom Armenia and its customers, the result is a second, independent westbound corridor — designed for scale, and engineered so that the dominant terrestrial transit geography of the region is no longer a single point of dependency.",
            },
          ].map((block, i) => (
            <motion.div
              key={block.heading}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              custom={i}
              className="bento-card"
            >
              <h2 className="text-xs font-semibold uppercase tracking-wider text-eyebrow mb-3">
                {block.heading}
              </h2>
              <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                {block.body}
              </p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Outcomes grid */}
      <section className="px-4 sm:px-6 lg:px-8 py-12">
        <div className="mx-auto max-w-4xl">
          <motion.h2
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-2xl sm:text-3xl font-bold text-foreground mb-8"
          >
            What the implementation delivers
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {outcomes.map((o, i) => (
              <motion.div
                key={o.title}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                custom={i}
                className="bento-card h-full"
              >
                <h3 className="text-base font-semibold text-foreground mb-2">
                  {o.title}
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {o.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Route breakdown */}
      <section className="px-4 sm:px-6 lg:px-8 py-12">
        <div className="mx-auto max-w-4xl">
          <motion.h2
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-2xl sm:text-3xl font-bold text-foreground mb-3"
          >
            The route, segment by segment
          </motion.h2>
          <p className="text-sm text-muted-foreground mb-8 max-w-2xl">
            Agarak → Tbilisi → Poti → CCS submarine → Balchik → Sofia →
            Frankfurt. Every segment is engineered to keep the end-to-end path
            off Türkiye terrestrial transit.
          </p>
          <ol className="space-y-4">
            {routeSegments.map((seg, i) => (
              <motion.li
                key={`${seg.from}-${seg.to}`}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                custom={i}
                className="bento-card"
              >
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-xs font-mono text-accent">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <p className="text-sm font-semibold text-foreground">
                    {seg.from}{" "}
                    <span className="text-muted-foreground">→</span>{" "}
                    {seg.to}
                  </p>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed pl-8">
                  {seg.detail}
                </p>
              </motion.li>
            ))}
          </ol>
        </div>
      </section>

      {/* Partner pull-quote */}
      <section className="px-4 sm:px-6 lg:px-8 py-12">
        <div className="mx-auto max-w-3xl">
          <motion.figure
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bento-card relative"
          >
            <svg
              className="absolute top-4 left-4 w-8 h-8 text-accent/30"
              fill="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path d="M9.983 3v7.391c0 5.704-3.731 9.57-8.983 10.609l-.995-2.151c2.432-.917 3.995-3.638 3.995-5.849h-4v-10h9.983zm14.017 0v7.391c0 5.704-3.748 9.571-9 10.609l-.996-2.151c2.433-.917 3.996-3.638 3.996-5.849h-3.983v-10h9.983z" />
            </svg>
            <blockquote className="pt-8 px-2">
              <p className="text-lg sm:text-xl text-foreground leading-relaxed font-light italic">
                &ldquo;Sofia Connect has been a reliable partner for TEAM
                Telecom across multiple fronts — from European DWDM capacity to
                the wider regional connectivity we build together. Their
                engineering team is sharp, provisioning is on-time, and they
                run their network to a standard we&apos;re comfortable
                recommending. A professional carrier to do business with.&rdquo;
              </p>
              <figcaption className="mt-6 text-sm">
                <span className="font-semibold text-foreground">
                  Davit Hayrapetyan
                </span>
                <span className="text-muted-foreground">
                  {" "}
                  — Head of International Carrier Relations and Wholesale
                  Business Division, TEAM Telecom Armenia
                </span>
              </figcaption>
            </blockquote>
          </motion.figure>
        </div>
      </section>

      {/* Why it matters */}
      <section className="px-4 sm:px-6 lg:px-8 py-12">
        <div className="mx-auto max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-5">
              Why this route matters
            </h2>
            <div className="space-y-4 text-base text-muted-foreground leading-relaxed">
              <p>
                For years, the practical options for westbound capacity out of
                the South Caucasus were limited: a terrestrial path through
                Türkiye, or a terrestrial path through Russia. Both routes
                concentrate risk — regulatory, political, and operational — on
                infrastructure that a carrier in Armenia or Georgia does not
                control.
              </p>
              <p>
                The Caucasus Cable System changed the shape of that map. By
                landing in Poti and Balchik, it creates a submarine alternative
                across the Black Sea that sidesteps Türkiye entirely. What
                TEAM Telecom Armenia and Sofia Connect have done together is
                wire that submarine leg into a real, production 400G service
                all the way from the Armenian border to Frankfurt — one of
                Europe&apos;s three dominant interconnection hubs.
              </p>
              <p>
                The result is not just more capacity. It is structural
                diversity for the region: a westbound corridor that does not
                share fate with the main Türkiye transit paths, engineered on
                modern coherent optics and carried end-to-end on operators
                that run their own backbones.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* About */}
      <section className="px-4 sm:px-6 lg:px-8 py-8">
        <div className="mx-auto max-w-3xl">
          <div className="bento-card bg-surface-light/50">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-eyebrow mb-3">
              About this story
            </h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              This case study describes a 400G DWDM implementation brought into
              production in Q1 2026 between TEAM Telecom Armenia and Sofia
              Connect. Published with the permission of TEAM Telecom Armenia.
              No specific customer performance numbers are disclosed; capacity,
              routing, and resilience characteristics are described at the
              network design level.
            </p>
            <ul className="mt-4 space-y-2 text-sm">
              <li>
                <span className="text-muted">Partner: </span>
                <a
                  href="https://www.teamtelecom.am/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-accent-text underline-offset-2 hover:underline"
                >
                  TEAM Telecom Armenia
                </a>
              </li>
              <li>
                <span className="text-muted">Submarine system: </span>
                Caucasus Cable System (Poti ↔ Balchik)
              </li>
              <li>
                <span className="text-muted">Sofia Connect network: </span>
                <Link href="/network" className="text-accent-text underline-offset-2 hover:underline">
                  Explore the backbone
                </Link>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 border-t border-border">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="text-2xl font-bold text-foreground mb-4">
            Need a westbound route out of the Caucasus?
          </h2>
          <p className="text-muted-foreground mb-8">
            400G and 100G DWDM wavelengths, L2VPN, and IP Transit from the
            Caucasus and Southeast Europe into Frankfurt and the wider European
            core — engineered for carriers, hyperscalers, and data centres.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              href="/contact"
              className="inline-flex items-center gap-2 px-6 py-3 text-sm font-medium bg-accent text-on-accent rounded-xl hover:bg-accent/90 transition-colors glow-teal"
            >
              Contact Sales
            </Link>
            <Link
              href="/network"
              className="inline-flex items-center gap-2 px-6 py-3 text-sm font-medium border border-border text-foreground rounded-xl hover:bg-surface-light transition-colors"
            >
              Explore the network
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
